// Home (Marko, 2026-11-01): the two dailies, a classic 9×9 and a variant, then the In progress
// and Library tiles, all on one phone screen without scrolling. The other layout B tiles (New from sources, Generate, the week's solves) and
// the earlier dailies strip are gone.
import {
  DAILY_KINDS,
  localDate,
  weekday,
  type DailyItem,
  type DailyKind,
  type HomeResponse,
} from '@zinc/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { ScoreChip } from '../components/ScoreChip';
import { Thumbnail } from '../components/Thumbnail';
import { StatusLine } from '../components/StatusLine';
import { LibraryIcon, PlayCircleIcon, PlayTriangleIcon, SearchIcon } from '../components/icons';
import { api } from '../lib/api';
import { libraryKeys } from '../lib/library';
import { useMe } from '../lib/queries';

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'Europe/Belgrade',
});

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Thu" within the last week, "24 Oct" before that. */
export function dayLabel(date: string, today: string): string {
  const days = Math.round((Date.parse(today) - Date.parse(date)) / 86_400_000);
  if (days >= 0 && days < 7) return DAYS[weekday(date)]!;
  const [, m, d] = date.split('-').map(Number) as [number, number, number];
  return `${d} ${MONTHS[m - 1]}`;
}

const KIND_NAMES: Record<DailyKind, string> = { classic: 'classic', variant: 'variant' };

function dailyHeading(kind: DailyKind, date: string, today: string): string {
  const name = KIND_NAMES[kind];
  if (date === today) return `Today's ${name}`;
  const days = Math.round((Date.parse(today) - Date.parse(date)) / 86_400_000);
  if (days === 1) return `Yesterday's ${name}`;
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} · ${dayLabel(date, today)}`;
}

function DailyCard({
  kind,
  daily,
  today,
}: {
  kind: DailyKind;
  daily: DailyItem | null;
  today: string;
}) {
  const me = useMe();
  const otherName = me.data?.other?.displayName ?? null;
  const label = `Today's ${KIND_NAMES[kind]} puzzle`;
  if (!daily) {
    return (
      <section
        aria-label={label}
        className="flex flex-col gap-1.5 rounded-[18px] border border-line bg-surface p-3.5"
      >
        <span className="text-[12px] font-semibold text-muted">
          {dailyHeading(kind, today, today)}
        </span>
        <span className="text-[15px]">The {KIND_NAMES[kind]} daily arrives at 06:00.</span>
      </section>
    );
  }
  const p = daily.puzzle;
  const mine = p.mine.progress;
  return (
    <section
      aria-label={label}
      className="flex gap-3.5 rounded-[18px] border border-line bg-surface p-3.5"
    >
      <Link to={`/puzzle/${p.id}`} tabIndex={-1} aria-hidden="true" className="shrink-0">
        <Thumbnail doc={daily.preview} size={150} />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {/* The score sits by the heading, so a card with a wrapped title and both people's status
            lines still leaves Home on one phone screen (Marko, 2026-11-01). */}
        <span className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-semibold text-muted">
            {dailyHeading(kind, daily.date, today)}
          </span>
          <ScoreChip score={p.status === 'needs-plugin' ? null : p.score} />
        </span>
        <Link
          to={`/library/${p.id}`}
          className="text-[17px] leading-[1.2] font-bold text-ink no-underline hover:text-ink"
        >
          {p.title}
        </Link>
        <div className="flex flex-col gap-1 text-[13px]">
          <StatusLine who="You" status={p.mine} own />
          {otherName && <StatusLine who={otherName} status={p.other} />}
        </div>
        <Link
          to={`/puzzle/${p.id}`}
          className="mt-auto flex h-11 items-center justify-center gap-2 rounded-[12px] bg-accent text-[15px] font-semibold text-white no-underline hover:text-white"
        >
          <PlayTriangleIcon />
          {mine === 'in-progress' ? 'Continue' : mine === 'solved' ? 'Open' : 'Play'}
        </Link>
      </div>
    </section>
  );
}

// One row (icon, then the count over its label), so Home fits a phone screen with two daily cards
// whose titles wrap (Marko's option B, 2026-11-01).
const TILE = 'box-border flex h-[72px] items-center gap-3 rounded-tile border px-3.5 no-underline';

function CountTile(props: {
  to: string;
  icon: React.ReactNode;
  count: number;
  label: string;
  testId: string;
}) {
  return (
    <Link
      to={props.to}
      data-testid={props.testId}
      className={`${TILE} border-line bg-surface text-ink hover:text-ink`}
    >
      <span className="flex shrink-0">{props.icon}</span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[26px] leading-none font-bold">{props.count}</span>
        <span className="truncate text-[14px] leading-[1.2] font-semibold">{props.label}</span>
      </span>
    </Link>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const home = useQuery({
    queryKey: libraryKeys.home,
    queryFn: () => api<HomeResponse>('/api/home'),
  });
  const today = localDate(new Date());
  return (
    <>
      <header className="mx-auto box-border flex w-full max-w-[720px] items-end gap-2 px-4 pt-4 pb-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[13px] text-muted">{dateFormat.format(new Date())}</span>
          <h1 className="m-0 text-[26px] font-bold tracking-[-0.02em]">ZincSudoku</h1>
        </div>
        <button
          type="button"
          aria-label="Search the library"
          onClick={() => void navigate('/library?search=1')}
          className="flex h-11 w-11 items-center justify-center rounded-btn border-0 bg-transparent p-0 text-ink"
        >
          <SearchIcon size={22} />
        </button>
      </header>
      <div className="mx-auto box-border flex w-full max-w-[720px] flex-col gap-3.5 px-4 pt-2 pb-4">
        {DAILY_KINDS.map((kind) =>
          home.isPending ? (
            <div key={kind} className="h-[180px] rounded-[18px] border border-line bg-surface" />
          ) : (
            <DailyCard
              key={kind}
              kind={kind}
              daily={home.data?.dailies[kind] ?? null}
              today={today}
            />
          ),
        )}
        <div className="grid grid-cols-2 gap-3">
          <CountTile
            to="/library?status=in-progress&sort=played"
            icon={<PlayCircleIcon className="text-accent" />}
            count={home.data?.inProgress ?? 0}
            label="In progress"
            testId="in-progress-tile"
          />
          <CountTile
            to="/library"
            icon={<LibraryIcon className="text-accent" />}
            count={home.data?.library ?? 0}
            label="Library"
            testId="library-tile"
          />
        </div>
      </div>
    </>
  );
}
